import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';

import './ItemLibrary.scss';

import { searchRegex } from 'utils';
import { WithIslandTooltip, WithItemTooltip, WithMoneyTooltip, WithMusicTooltip, WithSocialActionTooltip } from 'tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { VirtualList } from 'ui/virtual';
import { underlinerFunc } from 'ui/select';
import Resizable from 'ui/resizable';
import Select from 'ui/select';
import EditingContext from 'store/editingContext';

export function ItemCategory({ category, root, active, setActive }) {
  const [expanded, setExpanded] = React.useState(false);
  const onToggle = React.useCallback(() => setExpanded(e => !e), []);
  const onActivate = React.useCallback(() => setActive(category), [setActive, category]);
  let children = category.children;
  if (children && category.sort) {
    children = children.slice().filter(c => Data.strings[c.tip]).sort((a, b) => Data.strings[a.tip].localeCompare(Data.strings[b.tip]));
  }
  return (
    <li className={classNames({"lap-expanded": expanded || root})}>
      <div className={classNames("lap-category-header", {"lap-active": active === category})}>
        <span onClick={onActivate} onDoubleClick={onToggle}>{(Data.strings[category.tip] !== 'enumnull' && _F(category.tip)) || _F('tip.name.enum_itemdictionarycategory_etc_etc')}</span>
        {!!(!root && category.children) && (
          <FontAwesomeIcon icon={faChevronDown} className="lap-category-toggle" onClick={onToggle}/>
        )}
      </div>
      {!!category.children && (
        <ul className="lap-category-list">
          {children.map((item, index) => <ItemCategory key={index} category={item} active={active} setActive={setActive}/>)}
        </ul>
      )}
    </li>
  );
}

const renderOption = ({name, color}) => <span className={`la-color-${color}`}>{name}</span>;

export default function ItemLibrary({ root, onClick, charClass, hideTree, iconBox }) {
  const [filter, setFilter] = EditingContext.useState("ItemLibraryFilter", {
    tier: null,
    grade: null,
    class: null,
    minLevel: null,
    maxLevel: null,
    search: '',
  });
  const onSetSearch = React.useCallback(e => {
    const search = e.target.value;
    setFilter(f => ({...f, search}));
  }, [setFilter]);
  const onClearSearch = React.useCallback(() => setFilter(f => ({...f, search: ''})), [setFilter]);
  const onSetTier = React.useCallback(opt => setFilter(f => ({...f, tier: opt ? opt.id : null})), [setFilter]);
  const onSetGrade = React.useCallback(opt => setFilter(f => ({...f, grade: opt ? opt.id : null})), [setFilter]);
  const onSetClass = React.useCallback(opt => setFilter(f => ({...f, class: opt ? opt.id : null})), [setFilter]);
  const onSetMinLevel = React.useCallback(e => {
    const value = e.target.value ? parseInt(e.target.value) : null;
    setFilter(f => ({...f, minLevel: value}));
  }, [setFilter]);
  const onSetMaxLevel = React.useCallback(e => {
    const value = e.target.value ? parseInt(e.target.value) : null;
    setFilter(f => ({...f, maxLevel: value}));
  }, [setFilter]);
  const [iconOnly, setIconOnly] = React.useState(false);
  const onClickIconOnly = React.useCallback(e => setIconOnly(!!e.target.checked), []);

  const filterClass = charClass || filter.class;
  React.useEffect(() => setFilter(filter => ({...filter, search: ''})), [setFilter]);

  const tierOptions = React.useMemo(() => [
    {id: 1, name: _F('sys.dic.text_tier_value', 1)},
    {id: 2, name: _F('sys.dic.text_tier_value', 2)},
    {id: 3, name: _F('sys.dic.text_tier_value', 3)},
  ], []);
  const gradeOptions = React.useMemo(() => [
    ...Data.itemGrades.filter((d, index) => index <= 7).map(({name, color}, index) => ({id: index, name: _F(name), color})),
  ], []);
  const classOptions = React.useMemo(() => [
    ...Object.entries(Data.playerClasses).filter(([id, cls]) => cls.parent).map(([id, cls]) => ({id, name: _F(cls.name)})),
  ], []);

  const [active, setActive] = React.useState(root);
  const onSetActive = React.useCallback(value => {
    setActive(value);
    onClearSearch();
  }, [onClearSearch]);

  const itemsRaw = React.useMemo(() => {
    switch (active.type) {
    case 'money': return Object.keys(Data.money).filter(id => Data.money[id].desc);
    case 'social': return Object.keys(Data.socialAction).sort((a, b) => Data.socialAction[a].name.localeCompare(Data.socialAction[b].name));
    case 'music': return Object.keys(Data.music).sort((a, b) => Data.music[a].name.localeCompare(Data.music[b].name));
    case 'island': return Object.keys(Data.zone).filter(id => Data.zone[id].island).sort((a, b) => Data.zone[a].name.localeCompare(Data.zone[b].name));
    default:
      return Object.keys(Data.items).filter(id => {
        if (active.money) return false;
        const item = Data.items[id];
        if (active.types && !active.types.has(item.category)) return false;
        if (active.filter && !active.filter(item)) return false;
        return true;
      }).sort((a, b) => {
        const ia = Data.items[a], ib = Data.items[b];
        if (ia.grade !== ib.grade) return ib.grade - ia.grade;
        return ia.name.localeCompare(ib.name);
      });
    }
  }, [active]);

  const searchRe = React.useMemo(() => filter.search.trim() && searchRegex(filter.search.trim(), true), [filter.search]);
  const searchNum = !isNaN(filter.serach) && parseInt(filter.search);

  const items = React.useMemo(() => {
    if (filter.tier == null && filter.grade == null && filterClass == null && filter.minLevel == null && filter.maxLevel == null && !searchRe) return itemsRaw;
    if (searchNum) return itemsRaw.filter(id => parseInt(id) === searchNum);
    return itemsRaw.filter(id => {
      if (active.type) {
        if (!searchRe) return true;
        switch (active.type) {
        case 'money': return Data.money[id]?.name.match(searchRe);
        case 'social': return Data.socialAction[id]?.name.match(searchRe);
        case 'music': return Data.music[id]?.name.match(searchRe);
        case 'island': return Data.zone[id]?.name.match(searchRe);
        default: return false;
        }
      } else {
        const item = Data.items[id];
        if (!item) return false;
        if (filter.tier != null && item.tier !== filter.tier) return false;
        if (filter.grade != null && item.grade !== filter.grade) return false;
        if (filterClass != null && item.class && item.class !== filterClass && item.class !== Data.playerClasses[filterClass]?.parent) return false;
        if (filter.minLevel != null && item.ilevel < filter.minLevel) return false;
        if (filter.maxLevel != null && item.ilevel > filter.maxLevel) return false;
        if (searchRe && !item.name.match(searchRe)) return false;
        return true;
      }
    });
  }, [filter, searchRe, searchNum, itemsRaw, filterClass, active.type]);

  const renderItem = React.useCallback(id => {
    if (active.type === 'money') {
      const money = Data.money[id];
      if (!money) return null;
      return (
        <li key={id}>
          <WithMoneyTooltip id={id}>
            <span onClick={e => onClick?.(e, id, active.type, iconOnly)}>
              {searchRe ? underlinerFunc(money.name, searchRe) : money.name}
            </span>
          </WithMoneyTooltip>
        </li>
      );
    } else if (active.type === 'social') {
      const social = Data.socialAction[id];
      if (!social) return null;
      return (
        <li key={id}>
          <WithSocialActionTooltip id={id}>
            <span onClick={e => onClick?.(e, id, active.type, iconOnly)}>
              {searchRe ? underlinerFunc(social.name, searchRe) : social.name}
            </span>
          </WithSocialActionTooltip>
        </li>
      );
    } else if (active.type === 'music') {
      const music = Data.music[id];
      if (!music) return null;
      return (
        <li key={id}>
          <WithMusicTooltip id={id}>
            <span onClick={e => onClick?.(e, id, active.type, iconOnly)}>
              {searchRe ? underlinerFunc(music.name, searchRe) : music.name}
            </span>
          </WithMusicTooltip>
        </li>
      );
    } else if (active.type === 'island') {
      const zone = Data.zone[id];
      if (!zone || !zone.island) return null;
      return (
        <li key={id}>
          <WithIslandTooltip id={id}>
            <span onClick={e => onClick?.(e, id, active.type, iconOnly)}>
              {searchRe ? underlinerFunc(zone.name, searchRe) : zone.name}
            </span>
          </WithIslandTooltip>
        </li>
      );
    } else {
      const item = Data.items[id];
      if (!item) return null;
      return (
        <li key={id}>
          <WithItemTooltip item={id}>
            <span className={`la-color-${Data.itemGrades[item.grade].color}`} onClick={e => onClick?.(e, id, null, iconOnly)}>
              {searchRe ? underlinerFunc(item.name, searchRe) : item.name}
            </span>
          </WithItemTooltip>
        </li>
      );
    }
  }, [searchRe, onClick, active.type, iconOnly]);
  
  return (
    <div className="lap-ItemLibrary">
      <div className="lap-item-filters">
        <div>
          <Select options={tierOptions}
                  value={tierOptions.find(opt => opt.id === filter.tier)}
                  onChange={onSetTier}
                  allowEmpty
                  placeholder={_F('sys.dic.all_tier')}/>
          <Select options={gradeOptions}
                  value={gradeOptions.find(opt => opt.id === filter.grade)}
                  onChange={onSetGrade}
                  renderOption={renderOption}
                  renderValue={renderOption}
                  allowEmpty
                  placeholder={_F('sys.market.item_grade_all')}/>
          <Select className="lap-filter-class"
                  options={classOptions}
                  value={classOptions.find(opt => opt.id === filterClass)}
                  onChange={onSetClass}
                  allowEmpty
                  disabled={!!charClass}
                  placeholder={_F('sys.dic.all_class')}/>
        </div>
        <div>
          <span className="lap-level">
            <span>{_F('sys.market.item_level')}</span>
            <input type="number" step={1} value={filter.minLevel ?? ''} onChange={onSetMinLevel} placeholder={_F('sys.auction.input_box_text_min_value')}/>
            <span>~</span>
            <input type="number" step={1} value={filter.maxLevel ?? ''} onChange={onSetMaxLevel} placeholder={_F('sys.auction.input_box_text_max_value')}/>
          </span>
          <span className="lap-search">
            <input placeholder={_F('sys.market.enter_name')} value={filter.search} onChange={onSetSearch}/>
            {!!filter.search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="lap-clear" onClick={onClearSearch}/>}
          </span>
        </div>
      </div>
      <div className="lap-main-area">
        {!hideTree && (
          <Resizable className="lap-ItemCategories" side="e">
            <ul>
              <ItemCategory category={root} root={true} active={active} setActive={onSetActive}/>
            </ul>
          </Resizable>
        )}
        <div className="lap-ItemList">
          {!!iconBox && (
            <label className="lap-icon-only">
              <input type="checkbox" checked={iconOnly} onChange={onClickIconOnly}/> Icon Only
            </label>
          )}
          {items.length ? (
            <VirtualList maxHeight={1000} itemHeight={22} items={items} renderItem={renderItem} className="lap-item-list"/>
          ) : (
            <ul className="lap-item-list">
              <li className="lap-disabled">{_F('sys.guidebook.search_error')}</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
